import { useEffect, useMemo } from 'react'
import { useSortBy, useTable } from 'react-table'
import { Form, Table } from 'react-bootstrap'
import { format, parseISO } from 'date-fns'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCaretDown,
  faCaretUp,
  faSort
} from '@fortawesome/free-solid-svg-icons'
import ReactPaginate from 'react-paginate'
import styled from 'styled-components'
import { PERMISSION, UserType } from '../../helpers'

const StyledIcon = styled(FontAwesomeIcon)`
  color: darkGray;
`
const StyledTh = styled.th`
  white-space: nowrap;
`

const ProjectUsersTable = ({
  users,
  userType,
  page,
  pageCount,
  onPageChange,
  sort,
  onSort,
  onUpdate
}) => {
  const columns = useMemo(
    () => [
      { Header: 'Group Name', accessor: 'affiliationName' },
      { Header: 'FirstName', accessor: 'firstName' },
      { Header: 'LastName', accessor: 'lastName' },
      { Header: 'Username', accessor: 'username' },
      {
        Header: userType === UserType.New ? 'Date of Request' : 'Last Login',
        accessor: userType === UserType.New ? 'dateOfRequest' : 'lastLogin',
        Cell: ({ value }) => {
          try {
            return value ? format(parseISO(value), 'Pp') : null
          } catch (e) {
            console.error(
              `Error parsing ${
                userType === UserType.New ? 'dateOfRequest' : 'lastLogin'
              }`,
              value,
              e.stack
            )
            return null
          }
        }
      },
      {
        Header: 'Permission',
        accessor: 'permission',
        Cell: ({ row, value }) => (
          <Form.Control
            as='select'
            value={value}
            aria-label='permission'
            onChange={(event) =>
              onUpdate(row.original.id, 'permission', event.target.value)
            }
          >
            <option value={PERMISSION.UNASSIGNED}>Unassigned</option>
            <option value={PERMISSION.VIEW}>Viewer</option>
            <option value={PERMISSION.EDIT}>Editor</option>
            <option value={PERMISSION.ADMIN}>Group Admin</option>
            {value === PERMISSION.UNASSIGNED || value === PERMISSION.DENIED ? (
              <option value={PERMISSION.DENIED}>Deny Request</option>
            ) : null}
          </Form.Control>
        )
      }
    ],
    [onUpdate, userType]
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { sortBy },
    setSortBy
  } = useTable(
    {
      columns,
      data: users,
      initialState: { sortBy: sort },
      manualSortBy: true,
      onUpdate
    },
    useSortBy
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setSortBy(sort), [sort])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => onSort(sortBy), [sortBy])

  return (
    <>
      <Table
        responsive
        bordered
        hover
        striped
        size='sm'
        className='user-admin-table'
        {...getTableProps()}
      >
        <thead>
          {headerGroups.map((headerGroup) => {
            const { key, ...headerProps } = headerGroup.getHeaderGroupProps()
            return (
              <tr key={key} {...headerProps}>
                {headerGroup.headers.map((column) => {
                  const { key: colKey, ...colProps } = column.getHeaderProps(
                    column.getSortByToggleProps({ title: undefined })
                  )
                  return (
                    <StyledTh key={colKey} {...colProps}>
                      {column.render('Header')}
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <FontAwesomeIcon
                            icon={faCaretDown}
                            size='lg'
                            className='ml-1'
                          />
                        ) : (
                          <FontAwesomeIcon
                            icon={faCaretUp}
                            size='lg'
                            className='ml-1'
                          />
                        )
                      ) : (
                        <StyledIcon icon={faSort} size='lg' className='ml-1' />
                      )}
                    </StyledTh>
                  )
                })}
              </tr>
            )
          })}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row)
            const { key, ...rowProps } = row.getRowProps()
            return (
              <tr key={key} {...rowProps}>
                {row.cells.map((cell) => {
                  const { key: cellKey, ...cellProps } = cell.getCellProps()
                  return (
                    <td key={cellKey} {...cellProps}>
                      {cell.render('Cell')}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </Table>
      <ReactPaginate
        pageCount={pageCount}
        pageRangeDisplayed={2}
        marginPagesDisplayed={1}
        previousLabel='<'
        nextLabel='>'
        breakClassName='page-item'
        breakLinkClassName='page-link'
        onPageChange={onPageChange}
        forcePage={page}
        containerClassName='pagination justify-content-center'
        pageClassName='page-item'
        pageLinkClassName='page-link'
        activeClassName='active'
        previousClassName='page-item'
        nextClassName='page-item'
        previousLinkClassName='page-link'
        nextLinkClassName='page-link'
      />
    </>
  )
}

export { ProjectUsersTable }
